import { useRef, useState } from 'react';
import jwtDecode from 'jwt-decode';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';

import { RootState } from 'store';
import { checkAccess, checkAccessByAdminRightLevel } from 'routes/PrivateRoute';
import { SidebarItemsProps } from './SidebarItems';
import { ROLES } from 'constant/PermissonConstant';

export const SingleLevelMenu = ({ item }: { item: SidebarItemsProps }) => {
  const { pathname } = useLocation();
  const { threadUserAsRole, accessToken } = useSelector((state: RootState) => state.auth);
  const decodeToken = accessToken && (jwtDecode(accessToken) as { adminRightLevel: number });
  const { t } = useTranslation();
  const navigate = useNavigate();
  const textRef = useRef<HTMLDivElement>(null);
  const [tooltipText, setTooltipText] = useState('');

  if (!checkAccess(item.allowedRoles, threadUserAsRole)) return null;

  if (
    threadUserAsRole === ROLES.ADMIN &&
    // @ts-ignore
    !checkAccessByAdminRightLevel(item.accessAdminRightLevel, decodeToken?.adminRightLevel || 0)
  )
    return null;

  const isActive = !!item.path && pathname.startsWith(item.path);

  const handleMouseEnter = () => {
    const el = textRef.current?.querySelector('.MuiTypography-root') as HTMLElement | null;
    if (el && el.scrollWidth > el.clientWidth) {
      setTooltipText(t(item.title));
    }
  };

  return (
    <Tooltip title={tooltipText} placement='right' arrow disableInteractive>
      <ListItemButton
        sx={{
          mx: 1,
          my: 0.3,
          borderRadius: '8px',
          borderLeft: isActive ? '3px solid #00B3DC' : '3px solid transparent',
          backgroundColor: isActive ? 'rgba(0, 179, 220, 0.12)' : 'transparent',
          transition: 'all 0.15s ease',
          py: 1,
          '&:hover': {
            backgroundColor: isActive ? 'rgba(0, 179, 220, 0.18)' : 'rgba(23, 26, 49, 0.7)',
          },
        }}
        onClick={() => {
          if (item.path) {
            navigate(item.path);
          }
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setTooltipText('')}
      >
        <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
        <ListItemText
          ref={textRef}
          primary={t(item.title)}
          primaryTypographyProps={{
            fontSize: 13,
            fontWeight: 600,
            color: isActive ? '#00B3DC' : '#fff',
            letterSpacing: 0.3,
            lineHeight: 1.4,
            noWrap: true,
          }}
        />
      </ListItemButton>
    </Tooltip>
  );
};
